import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import { getProjectExportProgress, getProjectExports } from '@src/apis/project';
import {
  EXPORT_HISTORY_LIMIT,
  EXPORT_HISTORY_TYPES,
  EXPORT_POLL_FAILURE_BUDGET,
  EXPORT_POLL_FIRST_INTERVAL,
  EXPORT_STATUS,
  FETCH_EXPORT_STATUS_INTERVAL,
} from '@src/constants/project';
import { collectPendingExportIds } from '@src/services/project';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';

/**
 * Load one page of export history into redux.
 *
 * The only caller is the history tab: on open, and on each scroll to the end.
 * No cursor means the first page, which replaces the list instead of merging,
 * so no row outlives the state the backend has moved past.
 */
export const fetchExportRecordsPage = async (
  projectId,
  dispatch,
  { cursor } = {},
) => {
  const response = await getProjectExports(projectId, {
    cursor,
    limit: EXPORT_HISTORY_LIMIT,
  });
  if (!response?.status) throw new Error('export history page was rejected');

  const { exports, pagination } = response.result || {};
  const page = (exports || []).filter((item) =>
    EXPORT_HISTORY_TYPES.includes(item?.type),
  );
  // `hasNextPage` without a cursor to follow is nothing to act on.
  const nextCursor =
    pagination?.hasNextPage && pagination?.nextCursor
      ? pagination.nextCursor
      : null;

  dispatch(
    actions.project.setExportRecordsPage({ records: page, nextCursor, cursor }),
  );
  return { hasMore: Boolean(nextCursor) };
};

/**
 * The one place a project asks how its exports are doing.
 *
 * Mounted once per project. Every export still running is asked about by its own
 * record id, and the answers land in redux in a single dispatch, so the export
 * form and the history tab read the same value and cannot disagree.
 *
 * A tick that cannot answer for a record spends one of that record's retries,
 * and only that record's: an export whose endpoint is unhappy must not drag a
 * healthy one to FAILURE.
 */
const useProjectExportPoll = ({ projectId }) => {
  const dispatch = useDispatch();
  const enabled = useExportHistoryEnabled();
  const exportsByType = useSelector((state) => state.project.exports);
  const items = useSelector((state) => state.project.exportRecords.items);

  const pendingIds = useMemo(
    () => collectPendingExportIds({ exports: exportsByType, items }),
    [exportsByType, items],
  );
  // The loop reads the current ids through a ref, so the array identity stays
  // out of the callback that asks about them.
  const pendingIdsRef = useRef(pendingIds);
  pendingIdsRef.current = pendingIds;
  // An entry already holding the answer must not stand in for a row still behind.
  const shownStatusRef = useRef([]);
  shownStatusRef.current = [
    new Map((items || []).map((item) => [item?.id, item?.status])),
    new Map(
      Object.values(exportsByType || {}).map((entry) => [
        entry?.requestId,
        entry?.status,
      ]),
    ),
  ];
  const failuresRef = useRef(new Map());
  const timeoutRef = useRef(null);
  // Keyed on which records run, not how many: an export started while another
  // is running would otherwise wait out a gap grown for the older one.
  const pendingKey = [...pendingIds].sort().join('|');

  const tick = useCallback(
    async (isCancelled) => {
      const ids = pendingIdsRef.current;
      if (!ids.length) return;

      const answers = await Promise.allSettled(
        ids.map((id) => getProjectExportProgress(projectId, id)),
      );
      // An answer that lands after its own run was replaced belongs to a loop that
      // no longer exists, and it can be older than one already written: applying it
      // moves a record back to a status the store has passed, and the next SUCCESS
      // after that reads as a second finished export. It must not spend a retry
      // either, since nothing is left to spend it for.
      if (isCancelled()) return;

      const failures = failuresRef.current;
      const answered = [];
      const spent = [];
      answers.forEach((answer, index) => {
        const id = ids[index];
        // A rejected envelope arrives as HTTP 200 and carries no status, so it is
        // a failed tick rather than an answer.
        const record =
          answer.status === 'fulfilled' && answer.value?.status
            ? answer.value.result
            : null;
        if (record) {
          failures.delete(id);
          const behind = shownStatusRef.current.some(
            (shown) => shown.has(id) && shown.get(id) !== record.status,
          );
          if (behind) answered.push({ ...record, id: record.id || id });
          return;
        }
        const count = (failures.get(id) || 0) + 1;
        if (count < EXPORT_POLL_FAILURE_BUDGET) {
          failures.set(id, count);
          return;
        }
        failures.delete(id);
        spent.push({ id, status: EXPORT_STATUS.FAILURE });
      });

      const records = [...answered, ...spent];
      if (records.length) dispatch(actions.project.setExportProgress(records));
      if (spent.length) {
        dispatch(
          actions.noti.push({ severity: 'error', message: 'exportError' }),
        );
      }
    },
    [dispatch, projectId],
  );

  useEffect(() => {
    if (!enabled || !projectId || !pendingKey) return undefined;

    // Each wait starts when the previous answer is in, so a slow endpoint
    // stretches the gaps rather than stacking requests.
    let cancelled = false;
    const isCancelled = () => cancelled;
    let wait = 0;
    const run = async () => {
      await tick(isCancelled);
      if (cancelled) return;
      wait = wait
        ? Math.min(wait * 2, FETCH_EXPORT_STATUS_INTERVAL)
        : EXPORT_POLL_FIRST_INTERVAL;
      timeoutRef.current = setTimeout(run, wait);
    };
    run();

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, [enabled, projectId, pendingKey, tick]);
};

export default useProjectExportPoll;
